// GerencialDashboard.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const VIGENCIA = 2025;

const CATEGORIAS_PERSONERIA = [
  'Especial',
  'Primera',
  'Segunda',
  'Tercera',
  'Cuarta',
  'Quinta',
  'Sexta',
];

const ENDPOINTS = {
  ley617: '/api/hacienda/indicadores-ley/',
  concejo: '/api/hacienda/concejo-honorarios/',
  personeria: '/api/hacienda/personeria/',
  ley9993: '/api/hacienda/ley-99-93/',
  piip: '/api/pdd_indicadores_gerencia.php',
  seguimiento: '/api/hacienda/seguimiento-presupuestal/',
  presupuesto: '/api/presupuesto.php',
};

const F = {
  ley9993: { valor: 'indicador_1pct_icld', icld: 'icld_recaudo' },
  seg: {
    nombre: 'nombre_fuente',
    ppto: 'ppto_definitivo',
    recaudo: 'recaudo',
    comp: 'compromisos',
  },
};

const safNum = (v) =>
  v === null || v === undefined || Number.isNaN(Number(v)) ? 0 : Number(v);

const pick = (obj, ...keys) => {
  for (const k of keys) {
    if (obj?.[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
};

const fmt = (val) => {
  const n = safNum(val);
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    maximumFractionDigits: 0,
  }).format(n);
};

const fmtNum = (val) => {
  const n = safNum(val);
  return new Intl.NumberFormat('es-CO', {
    maximumFractionDigits: 2,
  }).format(n);
};

const fmtCompact = (val) => {
  const n = safNum(val);
  if (n >= 1e12) return `$${(n / 1e12).toFixed(2)} B`;
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)} MM`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(2)} M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(0)} K`;
  return `$${n.toFixed(0)}`;
};

const apiFetch = async (url, params = {}) => {
  const token =
    localStorage.getItem('auth_token') ||
    sessionStorage.getItem('auth_token') ||
    localStorage.getItem('token') ||
    localStorage.getItem('authToken');

  const qs = new URLSearchParams({ vigencia: VIGENCIA, ...params }).toString();

  const res = await fetch(`${url}?${qs}`, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
};

const card = {
  background: '#fff',
  borderRadius: 14,
  border: '1px solid #e2e8f0',
  boxShadow: '0 2px 12px rgba(30,58,95,.07)',
  overflow: 'hidden',
};

const BLUE = '#1e3a5f';
const BLUE2 = '#2563eb';

function Gauge({
  value = 0,
  limit = 100,
  max,
  title,
  subtitle,
  icon = '📊',
  unit = '%',
  loading,
  error,
}) {
  const CX = 118;
  const CY = 126;
  const R = 96;
  const SW = 18;
  const safeMax = max > 0 ? max : Math.max(limit * 1.4, safNum(value) * 1.1, 1);
  const safeVal = Math.min(Math.max(safNum(value), 0), safeMax);
  const safeLim = safNum(limit) || 1;

  const p2c = (deg, r = R) => ({
    x: CX + r * Math.cos((deg * Math.PI) / 180),
    y: CY - r * Math.sin((deg * Math.PI) / 180),
  });

  const arc = (a1, a2) => {
    if (Math.abs(a1 - a2) < 0.3) return '';
    const s = p2c(a1);
    const e = p2c(a2);
    const large = a1 - a2 > 180 ? 1 : 0;
    return `M ${s.x.toFixed(2)} ${s.y.toFixed(2)} A ${R} ${R} 0 ${large} 1 ${e.x.toFixed(2)} ${e.y.toFixed(2)}`;
  };

  const toAng = (v) => 180 - (v / safeMax) * 180;
  const vAng = toAng(safeVal);
  const lAng = toAng(safeLim);
  const g2 = toAng(safeLim * 0.65);
  const y2 = toAng(safeLim * 0.92);

  const col =
    safeVal <= safeLim * 0.65 ? '#16a34a' : safeVal <= safeLim * 0.92 ? '#d97706' : '#dc2626';
  const colBg =
    safeVal <= safeLim * 0.65 ? '#f0fdf4' : safeVal <= safeLim * 0.92 ? '#fffbeb' : '#fef2f2';
  const colBdr =
    safeVal <= safeLim * 0.65 ? '#bbf7d0' : safeVal <= safeLim * 0.92 ? '#fde68a' : '#fecaca';
  const statusTxt =
    safeVal <= safeLim * 0.65
      ? '✅ Dentro del límite'
      : safeVal <= safeLim * 0.92
      ? '⚠️ Cerca del límite'
      : '🚨 Límite superado';

  const nTip = p2c(vAng, R - 18);
  const nL = p2c(vAng + 90, 10);
  const nR = p2c(vAng - 90, 10);
  const lm1 = p2c(lAng, R - SW - 2);
  const lm2 = p2c(lAng, R + 10);

  const pctVsLim = safeLim > 0 ? (safeVal / safeLim) * 100 : 0;
  const isMoney = unit === '';

  const displayValue = isMoney ? fmt(safeVal) : `${safeVal.toFixed(2)}${unit}`;
  const displayLimit = isMoney ? fmt(safeLim) : `${safeLim.toFixed(2)}${unit}`;

  const displayValueGauge = isMoney ? fmtCompact(safeVal) : `${safeVal.toFixed(2)}${unit}`;
  const displayAux = isMoney
    ? fmtCompact(safeMax)
    : safeMax % 1 === 0
    ? safeMax
    : safeMax.toFixed(1);

  return (
    <div
      style={{
        ...card,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'stretch',
        minHeight: 180,
      }}
    >
      <div style={{ width: 6, background: col, flexShrink: 0 }} />

      <div
        style={{
          flex: '0 0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '10px 0 10px 10px',
          background: '#fafbfc',
          borderRight: '1px solid #f1f5f9',
        }}
      >
        {loading ? (
          <div
            style={{
              width: 230,
              height: 150,
              background: '#f1f5f9',
              borderRadius: 8,
              animation: 'gPulse 1.5s infinite',
            }}
          />
        ) : (
          <svg viewBox="0 0 236 160" style={{ width: 236, height: 160 }}>
            <path d={arc(180, 0)} fill="none" stroke="#e2e8f0" strokeWidth={SW} strokeLinecap="butt" />
            <path d={arc(180, g2)} fill="none" stroke="#86efac" strokeWidth={SW} />
            {g2 > y2 && <path d={arc(g2, y2)} fill="none" stroke="#fde047" strokeWidth={SW} />}
            {y2 > 0 && <path d={arc(y2, 0)} fill="none" stroke="#fca5a5" strokeWidth={SW} />}

            <line
              x1={lm1.x.toFixed(2)}
              y1={lm1.y.toFixed(2)}
              x2={lm2.x.toFixed(2)}
              y2={lm2.y.toFixed(2)}
              stroke="#dc2626"
              strokeWidth="3.5"
              strokeLinecap="round"
            />
            <circle cx={lm2.x.toFixed(2)} cy={lm2.y.toFixed(2)} r="4" fill="#dc2626" />

            <polygon
              points={`${nTip.x.toFixed(1)},${nTip.y.toFixed(1)} ${nL.x.toFixed(1)},${nL.y.toFixed(
                1
              )} ${nR.x.toFixed(1)},${nR.y.toFixed(1)}`}
              fill="#16a34a"
              opacity="0.55"
            />

            <circle cx={CX} cy={CY} r="10" fill="#1e293b" />
            <circle cx={CX} cy={CY} r="4" fill="#f0f4f8" />

            <text x={CX - R - 6} y={CY + 20} textAnchor="middle" fontSize="10" fill="#94a3b8">
              0
            </text>
            <text x={CX + R + 6} y={CY + 20} textAnchor="middle" fontSize="10" fill="#94a3b8">
              {displayAux}
            </text>

            <text
              x={CX}
              y={CY - 28}
              textAnchor="middle"
              fontSize={isMoney ? 18 : 22}
              fontWeight="900"
              fill="#0f766e"
              stroke="#ffffff"
              strokeWidth="1.2"
              paintOrder="stroke"
            >
              {displayValueGauge}
            </text>
          </svg>
        )}
      </div>

      <div
        style={{
          flex: 1,
          padding: '18px 22px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: 10,
        }}
      >
        <div>
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: '#94a3b8',
              textTransform: 'uppercase',
              letterSpacing: '.5px',
            }}
          >
            {icon} Indicador
          </span>
          <h2
            style={{
              margin: '4px 0 0',
              fontSize: 18,
              fontWeight: 800,
              color: BLUE,
              lineHeight: 1.2,
            }}
          >
            {title}
          </h2>
          {subtitle && <p style={{ margin: '3px 0 0', fontSize: 12, color: '#64748b' }}>{subtitle}</p>}
        </div>

        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            background: colBg,
            border: `1px solid ${colBdr}`,
            borderRadius: 20,
            padding: '5px 14px',
            fontSize: 13,
            fontWeight: 700,
            color: col,
            alignSelf: 'flex-start',
          }}
        >
          {statusTxt}
        </div>

        <div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: 11,
              color: '#64748b',
              marginBottom: 5,
            }}
          >
            <span>Ejecución vs límite</span>
            <span style={{ fontWeight: 700, color: col }}>{pctVsLim.toFixed(1)}%</span>
          </div>
          <div style={{ background: '#f1f5f9', borderRadius: 6, height: 10, overflow: 'hidden' }}>
            <div
              style={{
                width: `${Math.min(pctVsLim, 100)}%`,
                height: '100%',
                borderRadius: 6,
                background: col,
                transition: 'width .6s ease',
              }}
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ minWidth: 110 }}>
            <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 2 }}>Valor actual</div>
            <div style={{ fontSize: 20, fontWeight: 900, color: col }}>{displayValue}</div>
          </div>
          <div style={{ minWidth: 110 }}>
            <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 2 }}>Límite</div>
            <div style={{ fontSize: 20, fontWeight: 900, color: '#dc2626' }}>{displayLimit}</div>
          </div>
          <div style={{ minWidth: 110 }}>
            <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 2 }}>% del límite</div>
            <div style={{ fontSize: 20, fontWeight: 900, color: '#475569' }}>
              {pctVsLim.toFixed(2)}%
            </div>
          </div>
        </div>

        {error && <p style={{ margin: 0, fontSize: 11, color: '#dc2626' }}>⚠ Sin datos ({error})</p>}
      </div>
    </div>
  );
}

function BigNumberCard({ valor, icld, loading, error }) {
  return (
    <div
      style={{
        ...card,
        padding: '14px 12px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 180,
      }}
    >
      <p style={{ margin: '0 0 10px', fontSize: 13, fontWeight: 700, color: BLUE, textAlign: 'center' }}>
        Indicador Ley 99/93
      </p>

      <div
        style={{
          background: 'linear-gradient(135deg,#eff6ff,#dbeafe)',
          borderRadius: 12,
          padding: '18px 24px',
          textAlign: 'center',
          width: '100%',
        }}
      >
        <p
          style={{
            margin: '0 0 6px',
            fontSize: 11,
            color: '#64748b',
            letterSpacing: '.5px',
            textTransform: 'uppercase',
          }}
        >
          1% Tasa Ambiental sobre ICLD
        </p>

        {loading ? (
          <div
            style={{
              width: 160,
              height: 40,
              background: '#bfdbfe',
              borderRadius: 6,
              margin: '8px auto',
              animation: 'gPulse 1.5s infinite',
            }}
          />
        ) : (
          <p style={{ margin: 0, fontSize: 30, fontWeight: 900, color: BLUE2, letterSpacing: '-1px' }}>
            {fmt(valor)}
          </p>
        )}

        <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #bfdbfe' }}>
          <p style={{ margin: 0, fontSize: 11, color: '#64748b' }}>Base ICLD (fuente 11001):</p>
          <p style={{ margin: '2px 0 0', fontSize: 14, fontWeight: 700, color: '#1d4ed8' }}>
            {loading ? '...' : fmt(icld)}
          </p>
        </div>
      </div>

      {error && <p style={{ margin: '8px 0 0', fontSize: 11, color: '#dc2626' }}>⚠ Sin datos ({error})</p>}
    </div>
  );
}

function Section({ title, icon, badge, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div style={card}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          width: '100%',
          padding: '14px 18px',
          display: 'flex',
          alignItems: 'center',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          borderBottom: open ? '1px solid #e2e8f0' : 'none',
          transition: 'background .15s',
        }}
      >
        <span style={{ fontSize: 20, marginRight: 10 }}>{icon}</span>
        <span style={{ flex: 1, textAlign: 'left', fontSize: 15, fontWeight: 700, color: BLUE }}>
          {title}
        </span>
        {badge != null && badge !== '' && (
          <span
            style={{
              fontSize: 11,
              background: '#eff6ff',
              color: BLUE2,
              padding: '3px 10px',
              borderRadius: 20,
              marginRight: 10,
              fontWeight: 600,
            }}
          >
            {badge}
          </span>
        )}
        <span
          style={{
            fontSize: 13,
            color: '#64748b',
            transition: 'transform .25s',
            display: 'inline-block',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        >
          ▼
        </span>
      </button>
      {open && <div>{children}</div>}
    </div>
  );
}

function StackedBars({ fuentes, consolidado, loading, error }) {
  if (loading) {
    return (
      <div style={{ padding: 32, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
        Cargando seguimiento...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 20, color: '#dc2626', fontSize: 13 }}>
        ⚠ {error}
      </div>
    );
  }

  if (!fuentes?.length) {
    return (
      <div style={{ padding: 20, color: '#94a3b8', fontSize: 13 }}>
        Sin datos de fuentes
      </div>
    );
  }

  const data = fuentes
    .map((f) => ({
      nombre: String(f[F.seg.nombre] || f.fuente_financiacion || ''),
      fuente: String(f.fuente_financiacion || ''),
      ppto: safNum(f[F.seg.ppto]),
      recaudo: safNum(f[F.seg.recaudo]),
      comp: safNum(f[F.seg.comp]),
    }))
    .sort((a, b) => b.ppto - a.ppto);

  const maxVal = Math.max(...data.flatMap((d) => [d.ppto, d.recaudo, d.comp]), 1);

  const COLORS = {
    ppto: '#3b82f6',
    recaudo: '#22c55e',
    comp: '#f59e0b',
  };

  const barTrackStyle = {
    width: '100%',
    height: 12,
    background: '#e5e7eb',
    borderRadius: 999,
    overflow: 'hidden',
  };

  const barFill = (value, color) => ({
    width: `${Math.max((value / maxVal) * 100, value > 0 ? 1.5 : 0)}%`,
    height: '100%',
    background: color,
    borderRadius: 999,
    transition: 'width .5s ease',
  });

  return (
    <div>
      {consolidado && (
        <div
          style={{
            display: 'flex',
            gap: 12,
            padding: '12px 20px',
            background: '#f8fafc',
            borderBottom: '1px solid #e2e8f0',
            flexWrap: 'wrap',
          }}
        >
          {[
            { label: 'Ppto Definitivo', val: consolidado.ppto_definitivo, color: COLORS.ppto },
            { label: 'Recaudo', val: consolidado.recaudo, color: COLORS.recaudo },
            { label: 'Compromisos', val: consolidado.compromisos, color: COLORS.comp },
          ].map(({ label, val, color }) => (
            <div
              key={label}
              style={{
                flex: '1 1 160px',
                background: '#fff',
                borderRadius: 8,
                padding: '8px 14px',
                borderLeft: `3px solid ${color}`,
                boxShadow: '0 1px 4px rgba(0,0,0,.05)',
              }}
            >
              <div style={{ fontSize: 10, color: '#64748b', marginBottom: 2 }}>{label}</div>
              <div style={{ fontSize: 17, fontWeight: 800, color }}>{fmt(val)}</div>
            </div>
          ))}
        </div>
      )}

      <div style={{ padding: '14px 18px 10px' }}>
        <div
          style={{
            display: 'flex',
            gap: 20,
            marginBottom: 14,
            fontSize: 11,
            color: '#475569',
            flexWrap: 'wrap',
          }}
        >
          {[
            [COLORS.ppto, 'Ppto Definitivo'],
            [COLORS.recaudo, 'Recaudo'],
            [COLORS.comp, 'Compromisos'],
          ].map(([c, l]) => (
            <span key={l} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span
                style={{
                  width: 12,
                  height: 12,
                  background: c,
                  borderRadius: 3,
                  display: 'inline-block',
                  opacity: 0.9,
                }}
              />
              {l}
            </span>
          ))}
        </div>

        <div
          style={{
            maxHeight: 720,
            overflowY: 'auto',
            paddingRight: 6,
          }}
        >
          {data.map((d, i) => (
            <div
              key={`${d.fuente}-${i}`}
              style={{
                padding: '12px 0',
                borderBottom: '1px solid #eef2f7',
              }}
            >
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: '#1e3a5f',
                  marginBottom: 8,
                  lineHeight: 1.35,
                }}
              >
                {d.nombre || d.fuente}
              </div>

              <div style={{ display: 'grid', gap: 8 }}>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '130px 1fr 150px',
                    gap: 10,
                    alignItems: 'center',
                  }}
                >
                  <div style={{ fontSize: 11, color: COLORS.ppto, fontWeight: 700 }}>Ppto Definitivo</div>
                  <div style={barTrackStyle}>
                    <div style={barFill(d.ppto, COLORS.ppto)} />
                  </div>
                  <div style={{ fontSize: 11, color: '#334155', textAlign: 'right', fontWeight: 700 }}>
                    {fmt(d.ppto)}
                  </div>
                </div>

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '130px 1fr 150px',
                    gap: 10,
                    alignItems: 'center',
                  }}
                >
                  <div style={{ fontSize: 11, color: COLORS.recaudo, fontWeight: 700 }}>Recaudo</div>
                  <div style={barTrackStyle}>
                    <div style={barFill(d.recaudo, COLORS.recaudo)} />
                  </div>
                  <div style={{ fontSize: 11, color: '#334155', textAlign: 'right', fontWeight: 700 }}>
                    {fmt(d.recaudo)}
                  </div>
                </div>

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '130px 1fr 150px',
                    gap: 10,
                    alignItems: 'center',
                  }}
                >
                  <div style={{ fontSize: 11, color: COLORS.comp, fontWeight: 700 }}>Compromisos</div>
                  <div style={barTrackStyle}>
                    <div style={barFill(d.comp, COLORS.comp)} />
                  </div>
                  <div style={{ fontSize: 11, color: '#334155', textAlign: 'right', fontWeight: 700 }}>
                    {fmt(d.comp)}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PresupuestoBars({
  distribucion,
  totalizador,
  secretariaSeleccionada,
  loading,
  error,
}) {
  if (loading) {
    return (
      <div style={{ padding: 28, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
        Cargando flujo presupuestal...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 20, color: '#dc2626', fontSize: 13 }}>
        ⚠ {error}
      </div>
    );
  }

  if (!distribucion?.length && !totalizador) {
    return (
      <div style={{ padding: 20, color: '#94a3b8', fontSize: 13 }}>
        Sin datos presupuestales por secretaría
      </div>
    );
  }

  const normalizarFila = (d, nombreDefault = 'Sin secretaría') => {
    const aprobadas = safNum(d?.necesidades_aprobadas);
    const rechazadas = safNum(d?.necesidades_rechazadas);
    const anuladas = safNum(d?.necesidades_anuladas);

    const solicitadas = safNum(
      pick(
        d,
        'necesidades_solicitadas',
        'total_necesidades',
        'necesidades_total',
        'solicitadas'
      )
    ) || aprobadas + rechazadas + anuladas;

    const total = safNum(pick(d, 'total', 'total_necesidades')) || solicitadas;

    return {
      secretaria: d?.secretaria || nombreDefault,
      solicitadas,
      aprobadas,
      rechazadas,
      anuladas,
      total,
      cdp: safNum(d?.cdp_aprobados),
      rp: safNum(d?.rp_aprobados),
    };
  };

  const resumenGeneral = normalizarFila(
    {
      secretaria: 'Resumen general',
      necesidades_solicitadas:
        safNum(
          pick(
            totalizador,
            'necesidades_solicitadas',
            'total_necesidades',
            'necesidades_total',
            'solicitadas'
          )
        ) ||
        distribucion.reduce((acc, d) => {
          const aprobadas = safNum(d?.necesidades_aprobadas);
          const rechazadas = safNum(d?.necesidades_rechazadas);
          const anuladas = safNum(d?.necesidades_anuladas);
          return (
            acc +
            (safNum(
              pick(
                d,
                'necesidades_solicitadas',
                'total_necesidades',
                'necesidades_total',
                'solicitadas'
              )
            ) || aprobadas + rechazadas + anuladas)
          );
        }, 0),
      necesidades_aprobadas:
        safNum(totalizador?.necesidades_aprobadas) ||
        distribucion.reduce((acc, d) => acc + safNum(d?.necesidades_aprobadas), 0),
      necesidades_rechazadas:
        safNum(totalizador?.necesidades_rechazadas) ||
        distribucion.reduce((acc, d) => acc + safNum(d?.necesidades_rechazadas), 0),
      necesidades_anuladas:
        safNum(totalizador?.necesidades_anuladas) ||
        distribucion.reduce((acc, d) => acc + safNum(d?.necesidades_anuladas), 0),
      total:
        safNum(pick(totalizador, 'total', 'total_necesidades')) ||
        distribucion.reduce((acc, d) => {
          const aprobadas = safNum(d?.necesidades_aprobadas);
          const rechazadas = safNum(d?.necesidades_rechazadas);
          const anuladas = safNum(d?.necesidades_anuladas);
          return (
            acc +
            (safNum(
              pick(
                d,
                'necesidades_solicitadas',
                'total_necesidades',
                'necesidades_total',
                'solicitadas'
              )
            ) || aprobadas + rechazadas + anuladas)
          );
        }, 0),
      cdp_aprobados:
        safNum(totalizador?.cdp_aprobados) ||
        distribucion.reduce((acc, d) => acc + safNum(d?.cdp_aprobados), 0),
      rp_aprobados:
        safNum(totalizador?.rp_aprobados) ||
        distribucion.reduce((acc, d) => acc + safNum(d?.rp_aprobados), 0),
    },
    'Resumen general'
  );

  const filasSecretarias = distribucion
    .map((d) => normalizarFila(d))
    .sort((a, b) => b.solicitadas - a.solicitadas);

  let data = [];
  if (secretariaSeleccionada === '__TODAS__') {
    data = filasSecretarias;
  } else if (secretariaSeleccionada) {
    data = filasSecretarias.filter((d) => d.secretaria === secretariaSeleccionada);
  } else {
    data = [resumenGeneral];
  }

  if (!data.length) {
    return (
      <div style={{ padding: 20, color: '#94a3b8', fontSize: 13 }}>
        Sin datos para la selección realizada
      </div>
    );
  }



const maxGlobal = Math.max(
  ...data.map((d) => Math.max(d.solicitadas, d.aprobadas, d.rechazadas, d.anuladas, d.total, d.cdp, d.rp)),
  1
);

  const COLORS = {
    solicitadas: '#64748b',
    aprobadas: '#16a34a',
    rechazadas: '#dc2626',
    anuladas: '#94a3b8',
    total: '#0f172a',
    cdp: '#2563eb',
    rp: '#7c3aed',
  };

  const legend = [
    [COLORS.solicitadas, 'Necesidades solicitadas'],
    [COLORS.aprobadas, 'Necesidades aprobadas'],
    [COLORS.rechazadas, 'Necesidades rechazadas'],
    [COLORS.anuladas, 'Necesidades anuladas'],
    [COLORS.total, 'Total'],
    [COLORS.cdp, 'CDP aprobados'],
    [COLORS.rp, 'RP aprobados'],
  ];

  const barTrackStyle = {
    width: '100%',
    height: 12,
    background: '#e5e7eb',
    borderRadius: 999,
    overflow: 'hidden',
  };

  const barFill = (value, max, color) => ({
    width: `${Math.max((value / Math.max(max, 1)) * 100, value > 0 ? 1.5 : 0)}%`,
    height: '100%',
    background: color,
    borderRadius: 999,
    transition: 'width .5s ease',
  });

  return (
    <div>
      <div style={{ padding: '14px 18px 10px' }}>
        <div
          style={{
            display: 'flex',
            gap: 20,
            marginBottom: 14,
            fontSize: 11,
            color: '#475569',
            flexWrap: 'wrap',
          }}
        >
          {legend.map(([c, l]) => (
            <span key={l} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span
                style={{
                  width: 12,
                  height: 12,
                  background: c,
                  borderRadius: 3,
                  display: 'inline-block',
                  opacity: 0.95,
                }}
              />
              {l}
            </span>
          ))}
        </div>

        <div
          style={{
            maxHeight: 720,
            overflowY: 'auto',
            paddingRight: 6,
          }}
        >
          {data.map((d, i) => (
            <div
              key={`${d.secretaria}-${i}`}
              style={{
                padding: '14px 0',
                borderBottom: '1px solid #eef2f7',
              }}
            >
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 800,
                  color: BLUE,
                  marginBottom: 10,
                  lineHeight: 1.35,
                }}
              >
                {d.secretaria}
              </div>

              <div style={{ display: 'grid', gap: 8 }}>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '190px 1fr 120px',
                    gap: 10,
                    alignItems: 'center',
                  }}
                >
                  <div style={{ fontSize: 11, color: COLORS.solicitadas, fontWeight: 700 }}>
                    Necesidades solicitadas
                  </div>
                  <div style={barTrackStyle}>
                    <div style={barFill(d.solicitadas, maxGlobal, COLORS.solicitadas)} />
                  </div>
                  <div style={{ fontSize: 11, color: '#334155', textAlign: 'right', fontWeight: 700 }}>
                    {fmtNum(d.solicitadas)}
                  </div>
                </div>

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '190px 1fr 120px',
                    gap: 10,
                    alignItems: 'center',
                  }}
                >
                  <div style={{ fontSize: 11, color: COLORS.aprobadas, fontWeight: 700 }}>
                    Necesidades aprobadas
                  </div>
                  <div style={barTrackStyle}>
                    <div style={barFill(d.aprobadas,   maxGlobal, COLORS.aprobadas)} />
                  </div>
                  <div style={{ fontSize: 11, color: '#334155', textAlign: 'right', fontWeight: 700 }}>
                    {fmtNum(d.aprobadas)}
                  </div>
                </div>

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '190px 1fr 120px',
                    gap: 10,
                    alignItems: 'center',
                  }}
                >
                  <div style={{ fontSize: 11, color: COLORS.rechazadas, fontWeight: 700 }}>
                    Necesidades rechazadas
                  </div>
                  <div style={barTrackStyle}>
                    <div style={barFill(d.rechazadas,  maxGlobal, COLORS.rechazadas)} />
                  </div>
                  <div style={{ fontSize: 11, color: '#334155', textAlign: 'right', fontWeight: 700 }}>
                    {fmtNum(d.rechazadas)}
                  </div>
                </div>

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '190px 1fr 120px',
                    gap: 10,
                    alignItems: 'center',
                  }}
                >
                  <div style={{ fontSize: 11, color: COLORS.anuladas, fontWeight: 700 }}>
                    Necesidades anuladas
                  </div>
                  <div style={barTrackStyle}>
                    <div style={barFill(d.anuladas,    maxGlobal, COLORS.anuladas)} />
                  </div>
                  <div style={{ fontSize: 11, color: '#334155', textAlign: 'right', fontWeight: 700 }}>
                    {fmtNum(d.anuladas)}
                  </div>
                </div>

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '190px 1fr 120px',
                    gap: 10,
                    alignItems: 'center',
                  }}
                >
                  <div style={{ fontSize: 11, color: COLORS.total, fontWeight: 700 }}>
                    Total
                  </div>
                  <div style={barTrackStyle}>
                    <div style={barFill(d.total,       maxGlobal, COLORS.total)} />
                  </div>
                  <div style={{ fontSize: 11, color: '#334155', textAlign: 'right', fontWeight: 700 }}>
                    {fmtNum(d.total)}
                  </div>
                </div>

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '190px 1fr 120px',
                    gap: 10,
                    alignItems: 'center',
                  }}
                >
                  <div style={{ fontSize: 11, color: COLORS.cdp, fontWeight: 700 }}>
                    CDP aprobados
                  </div>
                  <div style={barTrackStyle}>
<div style={barFill(d.cdp, maxGlobal, COLORS.cdp)} />
                  </div>
                  <div style={{ fontSize: 11, color: '#334155', textAlign: 'right', fontWeight: 700 }}>
                    {fmtNum(d.cdp)}
                  </div>
                </div>

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '190px 1fr 120px',
                    gap: 10,
                    alignItems: 'center',
                  }}
                >
                  <div style={{ fontSize: 11, color: COLORS.rp, fontWeight: 700 }}>
                    RP aprobados
                  </div>
                  <div style={barTrackStyle}>
<div style={barFill(d.rp, maxGlobal, COLORS.rp)} />
                  </div>
                  <div style={{ fontSize: 11, color: '#334155', textAlign: 'right', fontWeight: 700 }}>
                    {fmtNum(d.rp)}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PIIPCards({ indicadores, loading, error }) {
  if (loading) {
    return (
      <div style={{ padding: 28, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
        Cargando PIIP...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 20, color: '#dc2626', fontSize: 13 }}>
        ⚠ {error}
      </div>
    );
  }

  if (!indicadores?.length) {
    return (
      <div style={{ padding: 20, color: '#94a3b8', fontSize: 13 }}>
        Sin indicadores PIIP cargados
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(260px,1fr))', gap: 12, padding: 16 }}>
      {indicadores.map((ind, i) => {
        const codigo = String(pick(ind, 'numero_indicador', 'codigo', 'id') || `IND-${i + 1}`);
        const nombre = String(
          pick(ind, 'nombre_resumido', 'nombre_completo', 'nombre', 'descripcion') || 'Indicador PIIP'
        );

        const meta = safNum(pick(ind, 'meta_cuatrenio', 'meta_total', 'meta', 'objetivo'));

        const ejec =
          safNum(ind['2024_t1']) +
          safNum(ind['2024_t2']) +
          safNum(ind['2024_t3']) +
          safNum(ind['2024_t4']) +
          safNum(ind['2025_t1']) +
          safNum(ind['2025_t2']) +
          safNum(ind['2025_t3']) +
          safNum(ind['2025_t4']) +
          safNum(ind['2026_t1']) +
          safNum(ind['2026_t2']) +
          safNum(ind['2026_t3']) +
          safNum(ind['2026_t4']) +
          safNum(ind['2027_t1']) +
          safNum(ind['2027_t2']) +
          safNum(ind['2027_t3']) +
          safNum(ind['2027_t4']);

        const porEjecutar = Math.max(meta - ejec, 0);
        const pct = meta > 0 ? Math.min((ejec / meta) * 100, 100) : 0;
        const col = pct >= 80 ? '#16a34a' : pct >= 50 ? '#d97706' : '#dc2626';
        const bgCol = pct >= 80 ? '#f0fdf4' : pct >= 50 ? '#fffbeb' : '#fef2f2';

        return (
          <div
            key={codigo}
            style={{
              background: '#fff',
              borderRadius: 10,
              padding: 14,
              border: '1px solid #e2e8f0',
              boxShadow: '0 1px 4px rgba(0,0,0,.05)',
            }}
          >
            <div style={{ fontSize: 11, fontWeight: 800, color: BLUE2, marginBottom: 3 }}>
              {codigo}
            </div>

            <div style={{ fontSize: 12, color: '#475569', marginBottom: 10, lineHeight: 1.45, minHeight: 34 }}>
              {nombre}
            </div>

            <div style={{ background: '#f1f5f9', borderRadius: 6, height: 10, overflow: 'hidden', marginBottom: 10 }}>
              <div
                style={{
                  width: `${pct}%`,
                  height: '100%',
                  background: col,
                  borderRadius: 6,
                  transition: 'width .6s ease',
                }}
              />
            </div>

            <div style={{ display: 'grid', gap: 6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                <span style={{ color: '#64748b' }}>Meta total</span>
                <b style={{ color: '#334155' }}>{fmtNum(meta)}</b>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                <span style={{ color: '#64748b' }}>Ejecución cuatrienio</span>
                <b style={{ color: '#334155' }}>{fmtNum(ejec)}</b>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                <span style={{ color: '#64748b' }}>Por ejecutar</span>
                <b style={{ color: '#dc2626' }}>{fmtNum(porEjecutar)}</b>
              </div>

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: 2,
                }}
              >
                <span style={{ fontSize: 11, color: '#64748b' }}>% avance</span>
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 800,
                    color: col,
                    background: bgCol,
                    padding: '2px 8px',
                    borderRadius: 20,
                  }}
                >
                  {pct.toFixed(1)}%
                </span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function GerencialDashboard() {
  const [data, setData] = useState({});
  const [ldg, setLdg] = useState({});
  const [errs, setErrs] = useState({});
  const [categoriaPersoneria, setCategoriaPersoneria] = useState('');
  const [secretariaPIIP, setSecretariaPIIP] = useState('');
  const [categoriaConcejo, setCategoriaConcejo] = useState('');
  const [secretariaPresupuesto, setSecretariaPresupuesto] = useState('');

  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem('auth_user');
    localStorage.removeItem('auth_token');
    sessionStorage.removeItem('auth_user');
    sessionStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('token');
    navigate('/logout', { replace: true });
  };

  useEffect(() => {
    const keys = Object.keys(ENDPOINTS);
    setLdg(Object.fromEntries(keys.map((k) => [k, true])));

    keys.forEach((key) => {
      apiFetch(ENDPOINTS[key], {})
        .then((d) => {
          setData((p) => ({ ...p, [key]: d }));
        })
        .catch((e) => {
          console.warn(`[Gerencial] Error en ${key}:`, e.message);
          setErrs((p) => ({ ...p, [key]: e.message }));
        })
        .finally(() => setLdg((p) => ({ ...p, [key]: false })));
    });
  }, []);

  const xGaugeLey617 = () => {
    const d = data.ley617;
    if (!d?.indicadores || !Array.isArray(d.indicadores) || !d.indicadores.length) {
      return { valor: 0, limite: 70, pct: 0, montoA: 0, montoB: 0 };
    }

    const filaVigencia =
      d.indicadores.find((x) => String(x.periodo).includes(String(VIGENCIA))) ||
      d.indicadores[d.indicadores.length - 1];

    return {
      valor: safNum(filaVigencia.indicador_pct),
      limite: safNum(filaVigencia.limite_pct ?? 70),
      pct: safNum(filaVigencia.indicador_pct),
      montoA: safNum(filaVigencia.A_recaudo),
      montoB: safNum(filaVigencia.B_compromisos),
    };
  };

  const xGaugeConcejo = () => {
    const d = data.concejo;

    if (!d?.icld) {
      return {
        valor: 0,
        limite: 0,
        max: 1,
        pct: 0,
        subtitulo: 'Sin datos de seguimiento ICLD',
      };
    }

    const limite = safNum(d.icld.limite_icld_1_5);
    const valor = safNum(d.icld.ppto_sec18_sin_honorarios);
    const pct = limite > 0 ? (valor / limite) * 100 : 0;
    const diferencia = limite - valor;

    return {
      valor,
      limite,
      max: Math.max(limite * 1.2, valor * 1.1, 1),
      pct,
      diferencia,
      subtitulo: `Seguimiento ICLD · Recaudo 11001: ${fmt(safNum(d.icld.recaudo_11001))} · Diferencia: ${fmt(diferencia)}`,
    };
  };

  const xGaugePersoneria = () => {
    const d = data.personeria;

    if (!categoriaPersoneria) {
      return { valor: 0, limite: 0, max: 1, pct: 0, subtitulo: 'Seleccione una categoría' };
    }

    if (!d?.presupuesto) {
      return { valor: 0, limite: 0, max: 1, pct: 0, subtitulo: 'Sin datos de personería' };
    }

    const gasto = safNum(d.presupuesto.ppto_sec20);
    const icld = safNum(d.presupuesto.icld_recaudo);
    const topes = Array.isArray(d.topes) ? d.topes : [];
    const smmlvs = Array.isArray(d.smmlvs) ? d.smmlvs : [];

    const topeRow = topes.find(
      (t) => String(t.categoria || '').toLowerCase() === categoriaPersoneria.toLowerCase()
    );

    let limite = 0;
    let subtitulo = `Categoría ${categoriaPersoneria}`;

    if (topeRow) {
      const tipo = String(topeRow.tipo || '').toLowerCase();

      if (tipo === 'pct_icld') {
        const pct = safNum(topeRow.pct_icld);
        limite = icld * (pct / 100);
        subtitulo = `Límite por % ICLD · Categoría ${categoriaPersoneria}`;
      }

      if (tipo === 'smmlv') {
        const smmlvRow = smmlvs.find((s) => Number(s.vigencia) === Number(VIGENCIA));
        const valorSmmlv = safNum(smmlvRow?.valor_smmlv);

        const base2024 = safNum(topeRow.smmlv_base_2024);
        const topeFinal = safNum(topeRow.smmlv_tope_final);
        const incremento = safNum(topeRow.incremento_anual);

        const smmlvTope = Math.min(
          base2024 + Math.max(Number(VIGENCIA) - 2024, 0) * incremento,
          topeFinal
        );

        limite = smmlvTope * valorSmmlv;
        subtitulo = `Límite por SMMLV · Categoría ${categoriaPersoneria}`;
      }
    }

    return {
      valor: gasto,
      limite,
      max: Math.max(limite * 1.2, gasto * 1.1, 1),
      pct: limite > 0 ? (gasto / limite) * 100 : 0,
      subtitulo,
    };
  };

  const xLey9993 = () => {
    const d = data.ley9993;
    if (!d) return { valor: 0, icld: 0 };

    return {
      valor: safNum(d.indicador_1pct_icld),
      icld: safNum(d.icld_recaudo),
    };
  };

  const xPIIP = () => {
    const d = data.piip;
    if (!d) return [];

    const lista =
      d.data || d.indicadores || d.items || d.rows || d.resultados || (Array.isArray(d) ? d : []);
    return Array.isArray(lista) ? lista : [];
  };

  const xSeg = () => {
    const d = data.seguimiento;
    if (!d) return { fuentes: [], consolidado: null };

    const fuentes = Array.isArray(d.consolidado) ? d.consolidado : [];

    const consolidado = fuentes.length
      ? {
          ppto_definitivo: fuentes.reduce((a, x) => a + safNum(x.ppto_definitivo), 0),
          recaudo: fuentes.reduce((a, x) => a + safNum(x.recaudo), 0),
          compromisos: fuentes.reduce((a, x) => a + safNum(x.compromisos), 0),
        }
      : null;

    return { fuentes, consolidado };
  };

  const xPresupuesto = () => {
    const d = data.presupuesto?.data || {};

    return {
      distribucion: Array.isArray(d.distribucion_secretarias) ? d.distribucion_secretarias : [],
      totalizador: d.totalizador || {
        necesidades_solicitadas: 0,
        necesidades_aprobadas: 0,
        necesidades_rechazadas: 0,
        necesidades_anuladas: 0,
        total: 0,
        cdp_aprobados: 0,
        rp_aprobados: 0,
      },
    };
  };

  const ley617 = xGaugeLey617();
  const concejo = xGaugeConcejo();
  const personeria = xGaugePersoneria();
  const ley9993_v = xLey9993();
  const piipList = xPIIP();
  const seg = xSeg();
  const presupuesto = xPresupuesto();

  const secretariasPIIP = [
    ...new Set(
      piipList
        .map((x) => String(x.dependencia || '').trim())
        .filter(Boolean)
    ),
  ].sort();

  const piipListFiltrado = secretariaPIIP
    ? piipList.filter((x) => String(x.dependencia || '').trim() === secretariaPIIP)
    : piipList;

  const secretariasPresupuesto = [
    ...new Set(
      presupuesto.distribucion
        .map((x) => String(x.secretaria || '').trim())
        .filter(Boolean)
    ),
  ].sort();

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#f0f4f8',
        padding: '22px 14px 40px',
        fontFamily: "'Inter','Segoe UI','Helvetica Neue',sans-serif",
      }}
    >
      <div style={{ maxWidth: 1460, margin: '0 auto' }}>
        <div
          style={{
            marginBottom: 22,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 12,
          }}
        >
          <div>
            <h1
              style={{
                margin: 0,
                fontSize: 24,
                fontWeight: 900,
                color: BLUE,
                letterSpacing: '-0.5px',
              }}
            >
              📊 Dashboard Gerencial
            </h1>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>
              Vigencia {VIGENCIA} · Sistema de Hacienda Presupuestal
            </p>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <div
              style={{
                fontSize: 12,
                color: '#94a3b8',
                padding: '6px 12px',
                background: '#fff',
                borderRadius: 8,
                border: '1px solid #e2e8f0',
              }}
            >
              {new Date().toLocaleDateString('es-CO', {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              })}
            </div>

            <button
              onClick={handleLogout}
              style={{
                background: '#ef4444',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                padding: '8px 14px',
                fontSize: 13,
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: '0 1px 4px rgba(0,0,0,.08)',
              }}
            >
              Cerrar sesión
            </button>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 14 }}>
          <Gauge
            value={ley617.valor}
            limit={ley617.limite}
            title="Ley 617 – Administración Central"
            subtitle={`Compromisos: ${fmt(ley617.montoB)} · Recaudo: ${fmt(ley617.montoA)} · Ejecución: ${ley617.pct.toFixed(2)}%`}
            icon="⚖️"
            unit="%"
            loading={ldg.ley617}
            error={errs.ley617}
          />

          <div style={{ ...card, padding: '14px' }}>
            <div
              style={{
                marginBottom: 12,
                display: 'flex',
                justifyContent: 'space-between',
                gap: 12,
                flexWrap: 'wrap',
              }}
            >
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: BLUE, marginBottom: 4 }}>
                  Configuración Concejo
                </div>
                <div style={{ fontSize: 11, color: '#64748b' }}>
                  Selecciona la categoría para calcular honorarios
                </div>
              </div>

              <div style={{ minWidth: 220 }}>
                <select
                  value={categoriaConcejo}
                  onChange={(e) => setCategoriaConcejo(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: 8,
                    border: '1px solid #cbd5e1',
                    background: '#fff',
                    fontSize: 13,
                    color: '#334155',
                  }}
                >
                  <option value="">Seleccione categoría</option>
                  {data.concejo?.categorias?.map((c) => (
                    <option key={c.categoria} value={c.categoria}>
                      {c.categoria}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <Gauge
              value={concejo.valor}
              limit={concejo.limite}
              max={concejo.max}
              title="Concejo Municipal"
              subtitle={`${concejo.subtitulo} · ${fmt(concejo.valor)} · ${concejo.pct.toFixed(2)}%`}
              icon="🏛️"
              unit=""
              loading={ldg.concejo}
              error={errs.concejo}
            />
          </div>

          <div style={{ ...card, padding: '14px' }}>
            <div
              style={{
                marginBottom: 12,
                display: 'flex',
                justifyContent: 'space-between',
                gap: 12,
                flexWrap: 'wrap',
              }}
            >
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: BLUE, marginBottom: 4 }}>
                  Configuración Personería
                </div>
                <div style={{ fontSize: 11, color: '#64748b' }}>
                  Selecciona la categoría para simular el límite legal
                </div>
              </div>

              <div style={{ minWidth: 220 }}>
                <select
                  value={categoriaPersoneria}
                  onChange={(e) => setCategoriaPersoneria(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: 8,
                    border: '1px solid #cbd5e1',
                    background: '#fff',
                    fontSize: 13,
                    color: '#334155',
                  }}
                >
                  <option value="">Seleccione categoría</option>
                  {CATEGORIAS_PERSONERIA.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <Gauge
              value={personeria.valor}
              limit={personeria.limite}
              max={personeria.max}
              title="Personería Municipal"
              subtitle={`${personeria.subtitulo} · Ejecutado: ${fmt(personeria.valor)} · ${personeria.pct.toFixed(2)}%`}
              icon="🔍"
              unit=""
              loading={ldg.personeria}
              error={errs.personeria}
            />
          </div>

          <BigNumberCard
            valor={ley9993_v.valor}
            icld={ley9993_v.icld}
            loading={ldg.ley9993}
            error={errs.ley9993}
          />
        </div>

        <div style={{ marginBottom: 14 }}>
          <Section
            title="PIIP – Seguimiento Plan de Desarrollo"
            icon="🎯"
            badge={piipListFiltrado.length ? `${piipListFiltrado.length} indicadores` : ''}
            defaultOpen={false}
          >
            <div style={{ padding: '14px 16px 0' }}>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                <div style={{ minWidth: 260 }}>
                  <select
                    value={secretariaPIIP}
                    onChange={(e) => setSecretariaPIIP(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: 8,
                      border: '1px solid #cbd5e1',
                      background: '#fff',
                      fontSize: 13,
                      color: '#334155',
                    }}
                  >
                    <option value="">Todas las secretarías</option>
                    {secretariasPIIP.map((sec) => (
                      <option key={sec} value={sec}>
                        {sec}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ fontSize: 12, color: '#64748b' }}>
                  {secretariaPIIP ? `Mostrando: ${secretariaPIIP}` : 'Mostrando todas las secretarías'}
                </div>
              </div>
            </div>

            <PIIPCards indicadores={piipListFiltrado} loading={ldg.piip} error={errs.piip} />
          </Section>
        </div>

        <div style={{ marginBottom: 14 }}>
          <Section
            title="Flujo Presupuestal"
            icon="📑"
            badge={
              secretariaPresupuesto
                ? secretariaPresupuesto === '__TODAS__'
                  ? `${presupuesto.distribucion.length} secretarías`
                  : '1 secretaría'
                : 'Resumen general'
            }
            defaultOpen={false}
          >
            <div style={{ padding: '14px 16px 0' }}>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                <div style={{ minWidth: 280 }}>
                  <select
                    value={secretariaPresupuesto}
                    onChange={(e) => setSecretariaPresupuesto(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: 8,
                      border: '1px solid #cbd5e1',
                      background: '#fff',
                      fontSize: 13,
                      color: '#334155',
                    }}
                  >
                    <option value="">Resumen general</option>
                    <option value="__TODAS__">Todas las secretarías</option>
                    {secretariasPresupuesto.map((sec) => (
                      <option key={sec} value={sec}>
                        {sec}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ fontSize: 12, color: '#64748b' }}>
                  {!secretariaPresupuesto
                    ? 'Mostrando resumen general'
                    : secretariaPresupuesto === '__TODAS__'
                    ? 'Mostrando todas las secretarías'
                    : `Mostrando: ${secretariaPresupuesto}`}
                </div>
              </div>
            </div>

            <PresupuestoBars
              distribucion={presupuesto.distribucion}
              totalizador={presupuesto.totalizador}
              secretariaSeleccionada={secretariaPresupuesto}
              loading={ldg.presupuesto}
              error={errs.presupuesto}
            />
          </Section>
        </div>

        <div>
          <Section
            title="Seguimiento Presupuestal"
            icon="💹"
            badge={seg.fuentes.length ? `${seg.fuentes.length} fuentes` : ''}
            defaultOpen={false}
          >
            <StackedBars
              fuentes={seg.fuentes}
              consolidado={seg.consolidado}
              loading={ldg.seguimiento}
              error={errs.seguimiento}
            />
          </Section>
        </div>

        <style>{`
          @keyframes gPulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.45; }
          }
        `}</style>
      </div>
    </div>
  );
}